/**
 * InlineDdlViewBodyBody — the embedded view-body editor content for
 * `CREATE VIEW` / `CREATE MATERIALIZED VIEW`, mounted directly (no trigger,
 * no Popover chrome) for the Compact VQE popover's `compactDdlBodyArm`.
 *
 * Seeds the form by running a lightweight lexical scan over the parked
 * statement text (`parseParkedViewStatement`) — DSE2 does not yet expose a
 * structured view arm in `extractDdlStructure`, so this path does its own
 * scan.
 *
 * Engine gating: sqlite has no atomic ALTER VIEW — renders a muted one-line
 * notice carrying the documented DROP-and-CREATE redirect copy. Other
 * engines mount the form which dispatches via `commitAlterView` (mssql
 * `OR ALTER`, pgsql / mysql / oracle `OR REPLACE`).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2 — View / Materialized View UI.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T4a/T5).
 */
import { useMemo } from 'react';

import type { QualifiedName } from '../../../sql-grammar/model';
import {
  getAlterViewUnsupportedTooltip,
  isAlterViewSupported,
} from '../edit/edits/alterView';
import type { VqeDdlEditContext } from '../edit/editContext';
import { ViewBodyEditFormBody } from '../render/common/ViewBodyEditFormBody';
import type { ViewOptionsValue } from '../render/common/ViewOptionsFields';
import {
  parseParkedViewStatement,
  type ParsedParkedView,
} from './parseParkedViewStatement';

const FALLBACK_VIEW_NAME: QualifiedName = {
  name: '',
  raw: '',
  quoted: false,
  foldedName: '',
};

function useParsedParkedView(
  editContext: VqeDdlEditContext,
  parsedOverride: ParsedParkedView | null | undefined,
): ParsedParkedView | null {
  return useMemo<ParsedParkedView | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedViewStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);
}

export interface InlineDdlViewBodyBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-view view (skips the scan). */
  readonly parsedOverride?: ParsedParkedView | null;
  /** Called after a successful commit (or Cancel) so the host can react. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

/**
 * Embedded body for the Compact VQE popover's `compactDdlBodyArm`, mounted
 * directly with no trigger/Popover chrome.
 */
export function InlineDdlViewBodyBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlViewBodyBodyProps) {
  const engine = editContext.service.engineId;
  const parsed = useParsedParkedView(editContext, parsedOverride);

  const viewName = parsed?.name ?? FALLBACK_VIEW_NAME;
  const initialBody = parsed?.bodyText ?? '';
  const initialOptions: ViewOptionsValue | undefined =
    parsed && parsed.columns.length > 0 ? { columns: parsed.columns } : undefined;

  if (!isAlterViewSupported(engine)) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-view-body-unsupported"
      >
        {getAlterViewUnsupportedTooltip(engine)}
      </p>
    );
  }

  return (
    <ViewBodyEditFormBody
      editContext={editContext}
      viewName={viewName}
      initialBody={initialBody}
      initialOptions={initialOptions}
      testId="inline-ddl-view-body-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
